import { LitElement, html, css } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { ChartNames } from '../../voy-timeseries-chart/src/echartUtils';
import '../../voyage-checkbox/src/voyage-checkbox';
import { VoyageCheckbox } from '../../voyage-checkbox/src/voyage-checkbox';

export type ChartsVisibility = {
    [index in ChartNames]: boolean
}

export enum ChartMode {
    ExecutionMode = "execution-mode",
    PlanningMode = "planning-mode"
}

export enum ChartSelectionEvents {
    ChartSelectionUpdated = "chart-options-updated",
    ChartComponentLoaded = "chart-selection-loaded"
};

@customElement('chart-selection')
export class ChartSelection extends LitElement {

    @property({ type: String, attribute: false }) chartsKey: ChartMode|null = null;
    @property({ type: Boolean, attribute: false }) maximized: boolean = false;

    static get styles() {
        return [
            css`
                .container {
                    background: var(--neutrals-grey-10, white);
                    display: grid;
                    grid-template-columns: 1fr 1fr 1fr;
                    grid-gap: 16px;
                    box-sizing: border-box;
                    padding: 16px 16px 6px 16px;
                    width: 100%;
                }

                .container[maximized] {
                    grid-template-columns: 1fr;
                    grid-auto-flow: column;
                    align-items: center;
                    justify-items: end;
                }

                .checkbox {
                    flex: 1 0 30%;
                    display: flex;
                    justify-content: left;
                    align-items: center;
                }

                .filler {
                    width: 40px;
                }
                .filler[maximized] {
                    display: none;
                }
        `];
    }

    @property({attribute: false}) private _options: ChartsVisibility = {
        sog: true,
        power: false,
        rpm: false,
        wind50M: false,
        currents: false,
        wave: false,
        swell: false,
        stw: false,
        currentImpact: false,
        rollAngle: false
    }
    
    render() {
        return html`
            <div class="container" ?maximized=${this.maximized}>
                <span class="checkbox">
                    <voyage-checkbox .label=${"SoG"} .checked=${this._options.sog} @input=${(e: Event) => this._optionClicked(ChartNames.Sog)}></voyage-checkbox>
                </span>

                <span class="checkbox">
                    <voyage-checkbox .label=${"StW"} .checked=${this._options.stw} @input=${(e: Event) => this._optionClicked(ChartNames.Stw)}></voyage-checkbox>
                </span>

                <span class="checkbox">
                    <voyage-checkbox .label=${"Current Impact"} .checked=${this._options.currentImpact} @input=${(e: Event) => this._optionClicked(ChartNames.CurrentImpact)}></voyage-checkbox>
                </span>

                <span class="checkbox">
                    <voyage-checkbox .label=${"ME Power"} .checked=${this._options.power} @input=${(e: Event) => this._optionClicked(ChartNames.Power)}></voyage-checkbox>
                </span>

                <span class="filler" ?maximized=${this.maximized}>
                    <!-- empty space filler -->
                </span>

                <span class="checkbox">
                    <voyage-checkbox .label=${"RPM"} .checked=${this._options.rpm} @input=${(e: Event) => this._optionClicked(ChartNames.Rpm)}></voyage-checkbox>
                </span>

                <span class="checkbox">
                    <voyage-checkbox .label=${"Wind50M"} .checked=${this._options.wind50M} @input=${(e: Event) => this._optionClicked(ChartNames.Wind50M)}></voyage-checkbox>
                </span>

                <span class="checkbox">
                    <voyage-checkbox .label=${"Currents"} .checked=${this._options.currents} @input=${(e: Event) => this._optionClicked(ChartNames.Currents)}></voyage-checkbox>
                </span>

                <span class="checkbox">
                    <voyage-checkbox .label=${"Wave"} .checked=${this._options.wave} @input=${(e: Event) => this._optionClicked(ChartNames.Wave)}></voyage-checkbox>
                </span>

                <span class="checkbox">
                    <voyage-checkbox .label=${"Swell"} .checked=${this._options.swell} @input=${(e: Event) => this._optionClicked(ChartNames.Swell)}></voyage-checkbox>
                </span>

                <span class="checkbox">
                    <voyage-checkbox .label=${"Roll Angle"} .checked=${this._options.rollAngle} @input=${(e: Event) => this._optionClicked(ChartNames.RollAngle)}></voyage-checkbox>
                </span>

                <span class="filler" ?maximized=${!this.maximized}>
                    <!-- empty space filler -->
                </span>
                
            </div>
        `;
    }

    firstUpdated() {
        if (!this.chartsKey) return;

        const res = JSON.parse(localStorage.getItem(this.chartsKey) || "null")
        if (res) {
            this._options = res;
            this.dispatchEvent(new CustomEvent(ChartSelectionEvents.ChartComponentLoaded, 
                {detail: {
                    options: this._options}
                }
            ))
        }
    }

    private async _optionClicked(optionKey: keyof ChartsVisibility) {
        this._options[optionKey] = !this._options[optionKey]

        // update radio button state before dispatching event
        await new Promise((resolve) => setTimeout(resolve));
        await this.updateComplete;

        this.dispatchEvent(new CustomEvent(ChartSelectionEvents.ChartSelectionUpdated, 
            {detail: {
                state: {
                    keyChanged: optionKey,
                    newValue: this._options[optionKey]
                }}
            }
        ))
        if (this.chartsKey) {
            localStorage.setItem(this.chartsKey, JSON.stringify(this._options));
        }
    }
};
